import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {Status} from '@domain/response-status';
import {User} from '@domain/user';
import {UserState} from '@domain/user-state';
import {UserType} from '@infrastructure/generic-auth/user-type.enum';
import {RequestStatus} from '@infrastructure/request-status.enum';
import {TranslateService} from '@ngx-translate/core';
import {catchError, EMPTY, map, Subject, switchMap, tap} from 'rxjs';
import {SnackBarService} from 'src/shared/snackbar/snackbar.service';

interface OwnerState extends UserState<User>
{
    owners: User[];
}

interface EmailParams
{
    email: string;
    subject: string;
    message: string;
    entityType?: UserType;
}

@Injectable({
    providedIn: 'root'
})
export class OwnerService
{
    private readonly httpClient = inject(HttpClient);

    private readonly translate = inject(TranslateService);

    private readonly snackBar = inject(SnackBarService);

    private readonly router = inject(Router);

    private readonly state = signal<OwnerState>({
        status: RequestStatus.NOT_EXECUTED,
        user: null,
        owners: []
    });

    private readonly emailState = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    private readonly passwordState = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    public readonly owner = computed(() => this.state().user);

    public readonly owners = computed(() => this.state().owners);

    public readonly loading = computed(() => this.state().status === RequestStatus.PENDING);

    public readonly emailStatus = computed(() => this.emailState().status);

    private readonly getDetails$ = new Subject<void>();

    public readonly ownerFetched$ = this.getDetails$.pipe(
        tap(() => this.state.set({...this.state(), status: RequestStatus.CONCLUDED})),
        switchMap(() =>
        {
            return this.httpClient.get('owner').pipe(
                map(User.fromJSON),
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR,
                        user: null
                    });

                    return EMPTY;
                })
            );
        })
    );

    private readonly update$ = new Subject<Partial<User>>();

    private readonly ownerUpdated$ = this.update$.pipe(
        switchMap((owner) =>
        {
            return this.httpClient.patch('owner', owner).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR,
                        user: this.owner()
                    });
                    
                    this.snackBar.showErrorSnackbar(this.translate.instant('PROFILE.ERROR_MESSAGE'));

                    return EMPTY;
                }),
                tap(() =>
                {
                    this.state.set({
                        ...this.state(),
                        user: new User(
                            this.owner().id,
                            this.owner().email,
                            owner.firstName,
                            owner.lastName,
                            owner.picture
                        )
                    });
                }));
        })
    );

    private readonly get$ = new Subject<void>();

    private readonly listFetched$ = this.get$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get<User[]>('owner/all');
        })
    );

    private readonly sendEmail$ = new Subject<EmailParams>();

    private readonly sendingEmail$ = this.sendEmail$.pipe(
        switchMap((emailParams: EmailParams) => this.httpClient.post('owner/send-mail-to-user', emailParams).pipe(
            catchError(() =>
            {
                this.emailState.set({
                    status: RequestStatus.ERROR
                });
                
                this.snackBar.showErrorSnackbar('GENERAL.RESPONSE_ERRORS.EMAIL_SEND_NOT_COMPLETE');
        
                return EMPTY;
            })
        ))
    );

    public constructor()
    {
        this.ownerFetched$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: (user: User) =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED,
                        user
                    });
                }
            });

        this.ownerUpdated$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: () =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED
                    });

                    this.getOwners();

                    this.snackBar.showSuccessSnackbar(this.translate.instant('PROFILE.SUCCESS_MESSAGE'));
                }
            });

        this.sendingEmail$.pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.emailState.set({
                    status: RequestStatus.CONCLUDED
                });
            }
        });

        this.listFetched$.pipe(takeUntilDestroyed()).subscribe({
            next: (owners: User[]) =>
            {
                this.state.set({
                    ...this.state(),
                    status: RequestStatus.CONCLUDED,
                    owners
                });
            }
        });
    }
    
    public getOwnerDetails(): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.getDetails$.next();
    }

    public getOwners(): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.get$.next();
    }

    public updateOwner(owner: Partial<User>): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.update$.next(owner);
    }
    
    public clearEmailStatus(): void
    {
        this.emailState.set({
            status: RequestStatus.NOT_EXECUTED
        });
    }

    public clearOwner(): void
    {
        this.state.set({
            ...this.state(),
            user: null
        });
    }

    public sendEmail(email: string, subject: string, message: string, entityType?: UserType): void
    {
        this.emailState.set({
            status: RequestStatus.PENDING
        });

        this.sendEmail$.next({email, subject, message, entityType});
    }
}
